import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { TFunction } from 'i18next';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  ContentBlockKind,
  CopilotConversation,
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import {
  CopilotChatInputPlaceholder,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
  CopilotPanelRuntimeView,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { TenantProcessStateEntityEvidenceAudit } from 'models/copilot/entityEvidenceAudit';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import Entity, { EntityTypes } from 'models/entity';
import { apiCopilotExecuteProcessEntityEvidenceAudit } from 'services/Api/copilotService';
import { ProgressHubProcess } from 'services/SignalR/ProgressHub';
import { newGuid } from 'utils/guid';
import { getEntityUrl } from 'utils/url';
import { getCopilotProcessWelcomeMessage } from '../copilotProcessMessages';
import { CopilotEntityEvidenceAuditPanelState, EntityEvidenceAuditTexts } from './copilotEntityEvidenceAuditPanelState';

// The families the audit runs on (spec 6.7): the action is bound only on their detail scenes
export const entityEvidenceAuditFamilies: EntityTypes[] = [
  EntityTypes.Asset,
  EntityTypes.Risk,
  EntityTypes.Process,
  EntityTypes.Objective,
  EntityTypes.ProcessingActivity,
];

// The submit actions of the closing card
const openTaskCardAction = 'entityAuditOpenTask';
const runAgainCardAction = 'entityAuditRunAgain';

/**
 * The AI evidence audit of one entity (PLAN-077) as the Generic usage drives it on the five families' detail scenes:
 * one execute starts it (the server prepares the sources and runs the judge in the background), the rail follows the
 * entity's row, the finished row brings the report as the final message and a card with "Open the assessment task"
 * when outcomes were applied, and "Run again" when the entity was unchanged since the stored report. Nothing is
 * typed while it runs.
 */
export class EntityEvidenceAuditProcess {
  static isAuditAction(action: CopilotAction | undefined): boolean {
    return action?.processId === TenantProcess.EntityEvidenceAudit;
  }

  static isRunning(runtime: Pick<CopilotPanelRuntimeView, 'action'>): boolean {
    return (
      EntityEvidenceAuditProcess.isAuditAction(runtime.action) &&
      runtime.action?.status === TenantProcessStatus.InProgress
    );
  }

  static getPanelState(
    runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>,
  ): CopilotEntityEvidenceAuditPanelState | undefined {
    return runtime.getUsageState<CopilotEntityEvidenceAuditPanelState>(CopilotPanelContext.EntityEvidenceAudit);
  }

  static getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    return EntityEvidenceAuditProcess.isAuditAction(action) ? getCopilotProcessWelcomeMessage(action, t) : undefined;
  }

  static canStartChat(runtime: CopilotPanelRuntimeView): boolean {
    return !EntityEvidenceAuditProcess.isRunning(runtime);
  }

  static getChatInputPlaceholder(runtime: CopilotPanelRuntimeView): CopilotChatInputPlaceholder | undefined {
    return EntityEvidenceAuditProcess.isRunning(runtime) ? CopilotChatInputPlaceholder.Working : undefined;
  }

  // The judge runs in the background host: the rail follows the entity's row, and its terminal push loads the row
  static getProgressRef(
    runtime: CopilotPanelRuntimeView,
    entityRef: CopilotPanelEntityRef | undefined,
  ): CopilotPanelProgressRef | undefined {
    if (!EntityEvidenceAuditProcess.isRunning(runtime)) return undefined;
    if (!entityRef?.entityId || !entityEvidenceAuditFamilies.includes(entityRef.entityType)) return undefined;

    return {
      ...entityRef,
      tenantProcess: TenantProcess.EntityEvidenceAudit,
      hubProcess: ProgressHubProcess.EntityEvidenceAudit,
    };
  }

  // the row is read on the rail's terminal push; a row the server has not finished yet is not the result
  static isPersistedProcessStateReady(state: CopilotPanelProcessState): boolean | undefined {
    if (state.processId !== TenantProcess.EntityEvidenceAudit) return undefined;

    return state.status === TenantProcessStatus.Completed || state.status === TenantProcessStatus.Failed;
  }

  /**
   * The start: Force when Run again queued it. A cache hit, a refusal or a failure answers finished in the same
   * request, so the process ends here; otherwise the rail takes over.
   */
  static async execute(runtime: CopilotPanelRuntime, entityRef: CopilotPanelEntityRef | undefined): Promise<void> {
    const { action } = runtime;
    if (!action || !EntityEvidenceAuditProcess.isAuditAction(action)) return;
    if (!entityRef?.entityId || !entityEvidenceAuditFamilies.includes(entityRef.entityType)) return;

    const panelState = EntityEvidenceAuditProcess.getPanelState(runtime);
    const force = panelState?.takeForce() ?? false;

    runtime.setIsLoading(true);
    const answer = await apiCopilotExecuteProcessEntityEvidenceAudit(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      entityRef.entityType,
      entityRef.entityId,
      force,
      runtime.connectionId,
      runtime.accessToken,
    );
    if (!answer) return;

    panelState?.setState(answer);
    if (answer.status !== TenantProcessStatus.Completed && answer.status !== TenantProcessStatus.Failed) {
      runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));

      return;
    }

    runtime.setConversation(
      EntityEvidenceAuditProcess.getClosedConversation(answer, answer.conversation, panelState?.texts),
    );
    await runtime.endProcess(TenantProcess.EntityEvidenceAudit);
  }

  static applyProcessState(
    runtime: CopilotPanelRuntime,
    state: CopilotPanelProcessState,
  ): CopilotPreviewContent | undefined {
    if (!(state.data instanceof TenantProcessStateEntityEvidenceAudit)) return undefined;

    const panelState = EntityEvidenceAuditProcess.getPanelState(runtime);
    panelState?.setState(state.data);
    runtime.setConversation(
      state.status === TenantProcessStatus.Completed
        ? EntityEvidenceAuditProcess.getClosedConversation(state.data, state.conversation, panelState?.texts)
        : state.conversation,
    );

    return undefined;
  }

  /**
   * The closing card's two buttons do their work here and return false, so the panel sends no chat turn: Open the
   * assessment task navigates, Run again queues Force and restarts the process, which begins a new conversation.
   */
  static onAdaptiveCardSubmit(runtime: CopilotPanelRuntime, payload: AdaptiveSubmit<JsonObject>): boolean {
    const submitted = payload.actionData.action;
    if (submitted === openTaskCardAction) {
      const taskId = EntityEvidenceAuditProcess.getPanelState(runtime)?.state?.appliedTaskId;
      if (taskId)
        runtime.appContext.copilotBridge.requestNavigation(getEntityUrl(new Entity(taskId, EntityTypes.Task)));

      return false;
    }

    if (submitted === runAgainCardAction && EntityEvidenceAuditProcess.isAuditAction(runtime.action)) {
      EntityEvidenceAuditProcess.getPanelState(runtime)?.queueForce();
      void runtime.startActiveProcess();
    }

    return false;
  }

  static clearPreview(runtime: CopilotPanelRuntime): void {
    EntityEvidenceAuditProcess.getPanelState(runtime)?.setState(undefined);
  }

  // The finished row's conversation with the report as the final message (unless the server wrote it there already)
  // and the closing card when it has a button to show
  private static getClosedConversation(
    state: TenantProcessStateEntityEvidenceAudit,
    rowConversation: CopilotConversation,
    texts: EntityEvidenceAuditTexts | undefined,
  ): CopilotConversation {
    const conversation = rowConversation.clone();
    const report = state.reportMarkdown?.trim();
    const hasReport = conversation.thread.some((item) => item.response?.trim() === report);
    if (report && !hasReport) {
      const item = new CopilotConversationItem();
      item.type = CopilotConversionItemType.Copilot;
      item.response = report;
      conversation.thread.push(item);
    }

    if (!texts) return conversation;

    const body: JsonObject[] = [];
    const actions: JsonObject[] = [];
    if (state.unchangedSince) {
      body.push({ type: 'TextBlock', text: texts.unchangedSince(state.unchangedSince), wrap: true });
      actions.push({ type: 'Action.Submit', title: texts.runAgain, data: { action: runAgainCardAction } });
    }
    if (state.appliedTaskId) {
      actions.push({ type: 'Action.Submit', title: texts.openTask, data: { action: openTaskCardAction } });
    }
    if (actions.length === 0) return conversation;

    const block = new CopilotConversationContentBlock();
    block.id = newGuid();
    block.kind = ContentBlockKind.AdaptiveCard;
    block.adaptiveCardJson = JSON.stringify({
      $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
      type: 'AdaptiveCard',
      version: '1.5',
      body,
      actions,
    });
    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.Copilot;
    item.contentBlocks = [block];
    conversation.thread.push(item);

    return conversation;
  }
}
